import React, { useEffect, useState } from "react";
import { Grid, Paper, Typography, CircularProgress, Pagination, Box, CardHeader, CardContent, TextField, InputAdornment, IconButton, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import { Spotify } from "react-spotify-embed";
import Card from "@mui/material/Card";
import Avatar from "@mui/material/Avatar";
import { Crumbs } from "../../Crumbs";
import SearchIcon from "@mui/icons-material/Search";
import Button from "@mui/material/Button";
import { Helmet } from "react-helmet";
import SubNav from "./SubNav.jsx";

const BACKEND_HOST = process.env["REACT_APP_BACKEND_HOST"];
const PAGE_SIZE = 9;

const CommunityPlaylistPage = () => {
    const [playlists, setPlaylists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [sort, setSort] = useState("createdAt:desc");

    useEffect(() => {
        setLoading(true);
        let url = `${BACKEND_HOST}/playlists?pagination[page]=${page}&pagination[pageSize]=${PAGE_SIZE}&sort[0]=${sort}&populate=profile.pic`;
        if (search) {
            url += `&filters[title][$containsi]=${encodeURIComponent(search)}`;
        }
        fetch(url)
            .then(res => res.json())
            .then(obj => {
                setPlaylists(obj.data || []);
                setTotalPages(obj.meta?.pagination?.totalPages || 1);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [page, search, sort]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        setSearch(searchInput.trim());
    };

    const handleSortChange = (e) => {
        setSort(e.target.value);
        setPage(1);
    };

    const desc = "Explore community-curated playlists from our dance community. Share your own playlists and discover new music!";
    const title = "Community Playlists";

    const meta = <Helmet>
                <title>{title}</title>
                <meta name="description" content={desc} />
                <meta charSet="utf-8"/>
                <meta property="og:description" content={desc}/>
                <meta property="twitter:title" content={title}/>
                <meta property="twitter:description" content={desc}/>
                <title>{title}</title>
                <meta property="description" content={desc} />
                <link rel="canonical" href={"/community/playlists"}/>
            </Helmet>

    if (loading) {
        return (
            <Paper style={{ padding: 20, margin: "auto" }}>
                {meta}
                <CircularProgress />
                <Typography variant="h6" align="center" style={{ marginTop: 10 }}>
                    Loading playlists...
                </Typography>
            </Paper>
        );
    }

    return (
        <Box>
            {meta}
            <Crumbs crumbs={[
                { title: "Community", href: "/community" },
                { title: "Playlists" }
            ]} />
            <SubNav />
            <Box my={5}>
                <Typography variant="h5" align="center" gutterBottom>
                    Community Playlists
                </Typography>
                <Typography variant="body1" align="center">
                    This section is curated by <strong>Ellie</strong> and features playlists contributed by our dance community. Enjoy discovering new music and sharing your favorites!
                </Typography>
                <Box fullWidth textAlign="center" pt={3}>
                    <Button href="/community/playlists/mine" variant="contained" color="secondary">
                        Add your own Playlists
                    </Button>
                </Box>
                <Box mt={3} display="flex" justifyContent="center" gap={2}>
                    <form onSubmit={handleSearch}>
                        <TextField
                            size="small"
                            placeholder="Search by playlist title"
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton type="submit" aria-label="search">
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </form>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="sort-label">Sort By</InputLabel>
                        <Select
                            labelId="sort-label"
                            value={sort}
                            label="Sort By"
                            onChange={handleSortChange}
                        >
                            <MenuItem value="createdAt:desc">Newest</MenuItem>
                            <MenuItem value="createdAt:asc">Oldest</MenuItem>
                            <MenuItem value="title:asc">Title (A-Z)</MenuItem>
                            <MenuItem value="title:desc">Title (Z-A)</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>
            <Grid container spacing={2} style={{ padding: 2 }}>
                {playlists.map((pl) => (
                    <Grid item xs={12} md={4} sm={6} key={pl.id}>
                        <Card>
                            <CardHeader
                                title={pl.title}
                                subheader={pl.profile?.name || "Kind Stranger"}
                                avatar={
                                    pl.profile?.pic?.formats?.thumbnail ? (
                                        <Avatar src={pl.profile.pic.formats.thumbnail.url} />
                                    ) : (
                                        <Avatar>{"?"}</Avatar>
                                    )
                                }
                            />
                            <CardContent style={{ textAlign: "center" }}>
                                <Spotify link={pl.link} />
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            <Box display="flex" justifyContent="center" mt={2}>
                <Pagination 
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                />
            </Box>
        </Box>
    );  
};  


export default CommunityPlaylistPage;